import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  RefreshCcw,
  Users,
  XCircle,
  Loader2,
} from "lucide-react";
import React, { useMemo } from "react";

interface DashboardProps {
  data?: any[];
  isLoading?: boolean;
}

const ValidationDashboard = ({
  data = [],
  isLoading = false,
}: DashboardProps) => {
  // 1. Optimasi Perhitungan Frontend dengan useMemo
  const { stats, total, processed, progressPercentage } = useMemo(() => {
    if (!data)
      return {
        stats: { valid: 0, belum: 0, rekon: 0 },
        total: 0,
        processed: 0,
        progressPercentage: 0,
      };

    const computedStats = data.reduce(
      (acc, curr) => {
        const status = curr.status_validasi;
        if (status === "REKON") acc.rekon++;
        else if (status === true || status === "true")
          acc.valid++; // Handle boolean string
        else acc.belum++;
        return acc;
      },
      { valid: 0, belum: 0, rekon: 0 },
    );

    const totalCount = data.length;
    const processedCount = computedStats.valid + computedStats.rekon;
    const percentage =
      totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0;

    return {
      stats: computedStats,
      total: totalCount,
      processed: processedCount,
      progressPercentage: percentage,
    };
  }, [data]);

  // Loading Skeleton Component
  const SkeletonCard = () => (
    <Card className="p-4 border-slate-200 dark:border-slate-800 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-200 dark:bg-slate-800 rounded-lg" />
        <div className="space-y-2">
          <div className="w-12 h-2 bg-slate-200 dark:bg-slate-800 rounded" />
          <div className="w-8 h-4 bg-slate-200 dark:bg-slate-800 rounded" />
        </div>
      </div>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="space-y-6 mb-8">
        <div className="flex justify-between items-end">
          <div className="space-y-2">
            <div className="h-8 w-64 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
            <div className="h-4 w-48 bg-slate-100 dark:bg-slate-900 rounded animate-pulse" />
          </div>
          <div className="h-10 w-20 bg-slate-200 dark:bg-slate-800 rounded animate-pulse" />
        </div>
        <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  const chartItems = [
    {
      label: "Sudah Valid",
      value: stats.valid,
      color: "bg-emerald-500",
      icon: <CheckCircle2 />,
    },
    {
      label: "Rekon",
      value: stats.rekon,
      color: "bg-destructive",
      icon: <RefreshCcw />,
    },
    {
      label: "Belum Valid",
      value: stats.belum,
      color: "bg-amber-500",
      icon: <XCircle />,
    },
  ];

  return (
    <div className="space-y-6 mb-8 transition-all duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            Progress Verifikasi Nilai Rapor
            {/* Indikator kecil jika ada perhitungan di background */}
            {isLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            )}
          </h2>
          <p className="text-muted-foreground text-sm">
            Memantau penyelesaian validasi berkas pendaftar secara real-time.
          </p>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-black text-primary tracking-tighter">
            {progressPercentage}%
          </span>
          <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Selesai
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <Progress value={progressPercentage} className="h-2" />
        <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70">
          <span>Mulai</span>
          <span>
            {processed} Diproses dari {total} Peserta
          </span>
          <span>Target 100%</span>
        </div>
      </div>

      {/* Grid Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-slate-50/50 dark:bg-slate-900/50 shadow-none border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 text-blue-600 rounded-lg">
              <Users className="w-5 h-5" />
            </div>
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Total
              </p>
              <h3 className="text-xl font-bold leading-none">{total}</h3>
            </div>
          </div>
        </Card>

        {chartItems.map((item, idx) => (
          <Card
            key={idx}
            className="p-4 border-slate-200 dark:border-slate-800"
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 ${item.color.replace("bg-", "bg-").replace("500", "500/10")} ${item.color.replace("bg-", "text-")} rounded-lg`}
              >
                {React.cloneElement(
                  item.icon as React.ReactElement<{ className: string }>,
                  {
                    className: "w-5 h-5",
                  },
                )}
              </div>
              <div className="space-y-0.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {item.label}
                </p>
                <h3 className="text-xl font-bold leading-none">{item.value}</h3>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ValidationDashboard;
